import { entries, record, scalar, type Answers, type Entry } from "./lib/form-model";

type SocialProfile = {
  platform: string;
  platformOther: string;
  handle: string;
  profileUrl: string;
  accountKind: string;
};

const workFields: Record<string, string> = {
  "pf.engagement.your_role": "role",
  "pf.engagement.duties": "duties",
  "pf.us.mptv_services": "film_or_television",
  "pf.es.audiovisual_project_role": "audiovisual_project_role",
  "pf.engagement.hiring_company": "hiring_company",
  "pf.engagement.hiring_company_role": "hiring_company_role",
  "pf.engagement.hiring_company_is_production_company": "same_as_production_company",
  "pf.engagement.engagement_type": "engagement_type",
  "pf.engagement.paid": "paid",
  "pf.engagement.compensation": "compensation",
  "pf.us.ownership_interest": "ownership_interest",
  "pf.engagement.engagement_dates": "engagement_dates",
  "pf.engagement.country_of_work": "country_of_work",
  "pf.engagement.work_location": "work_location",
  "pf.engagement.dates_in_country": "dates_in_country",
  "q.travel.expected_arrival": "expected_arrival",
  "pf.es.health_cover_source": "health_cover_source",
  "pf.es.schengen_entry_date": "schengen_entry_date",
  "pf.it.accommodation": "accommodation",
  "pf.it.health_insurance": "health_insurance",
};

const YOUR_WORK_KEYS = new Set(["role", "duties", "film_or_television", "audiovisual_project_role"]);
const HIRING_KEYS = new Set([
  "hiring_company",
  "hiring_company_role",
  "same_as_production_company",
  "engagement_type",
  "paid",
  "compensation",
  "ownership_interest",
]);
const DATES_KEYS = new Set([
  "engagement_dates",
  "country_of_work",
  "work_location",
  "dates_in_country",
  "expected_arrival",
  "health_cover_source",
  "schengen_entry_date",
]);
const WORK_PAGE_ORDER = ["your_work", "hiring_and_pay", "dates_and_travel", "italy_work"] as const;

type WorkPage = (typeof WORK_PAGE_ORDER)[number];

function workPageForFieldId(id: string): WorkPage | null {
  if (id.startsWith("pf.it.")) return "italy_work";
  const key = workFields[id];
  if (!key) return null;
  if (YOUR_WORK_KEYS.has(key)) return "your_work";
  if (HIRING_KEYS.has(key)) return "hiring_and_pay";
  if (DATES_KEYS.has(key)) return "dates_and_travel";
  return null;
}

export function socialMediaPayload(profiles: SocialProfile[]) {
  return {
    accounts: profiles.map((profile) => ({
      platform: profile.platform,
      platform_other: profile.platformOther || null,
      handle: profile.handle || null,
      profile_url: profile.profileUrl || null,
      account_kind: profile.accountKind || null,
    })),
  };
}

export function socialProfilesFromApi(data: { accounts?: Array<Record<string, string | null>> } | null | undefined): SocialProfile[] {
  return (data?.accounts || []).map((account) => ({
    platform: account.platform || "",
    platformOther: account.platform_other || "",
    handle: account.handle || "",
    profileUrl: account.profile_url || "",
    accountKind: account.account_kind || "",
  }));
}

export function travelDocumentsPayload(answers: Answers) {
  return {
    documents: entries(answers["pf.passport.other_details"]).map((item) => ({
      document_kind: item.document_kind,
      document_number: item.number,
      issuing_country: item.issuing_country,
      issue_date: item.issue_date || null,
      expiry_date: item.expiry_date,
    })),
  };
}

export function travelDocumentsFromApi(data: { documents?: Array<Record<string, string | null>> } | null | undefined): Entry[] {
  return (data?.documents || []).map((item) => ({
    document_kind: item.document_kind || "",
    number: item.document_number || "",
    issuing_country: item.issuing_country || "",
    issue_date: item.issue_date || "",
    expiry_date: item.expiry_date || "",
  }));
}

export function productionWorkPayload(ids: string[], answers: Answers) {
  const page = workPageForFieldId(ids.find((id) => workPageForFieldId(id) != null) || "") || "your_work";
  const body: Record<string, unknown> = { page };
  for (const id of ids) {
    const key = workFields[id];
    if (!key || workPageForFieldId(id) !== page) continue;
    const value = answers[id];
    if (key === "engagement_dates" || key === "dates_in_country") {
      const span = record(value);
      const start = span.start || null;
      const end = span.end || null;
      if (start || end) body[key] = { start, end };
    } else if (key === "compensation") {
      if (scalar(answers["pf.engagement.paid"]).trim().toLowerCase() === "yes") {
        const money = record(value);
        if (money.amount || money.currency) body[key] = { amount: money.amount || null, currency: money.currency || null };
      } else {
        body[key] = null;
      }
    } else if (key === "health_insurance") {
      const insurance = record(value);
      const answer = (insurance.value || scalar(value) || "").trim().toLowerCase() || null;
      if (answer === "no") body[key] = { answer: "no" };
      else if (answer) body[key] = { answer, details: insurance.explanation || null };
    } else {
      const text = scalar(value).trim();
      if (text) body[key] = text;
    }
  }
  return body;
}

/** One POST body per work page. The API rejects fields that belong on another page. */
export function productionWorkPayloads(ids: string[], answers: Answers) {
  const grouped = new Map<WorkPage, string[]>();
  for (const id of ids) {
    const page = workPageForFieldId(id);
    if (!page) continue;
    const list = grouped.get(page) || [];
    list.push(id);
    grouped.set(page, list);
  }
  return WORK_PAGE_ORDER.filter((page) => grouped.has(page)).map((page) =>
    productionWorkPayload(grouped.get(page) || [], answers),
  );
}

export function answersFromProductionWork(data: {
  your_work?: Record<string, unknown> | null;
  hiring_and_pay?: Record<string, unknown> | null;
  dates_and_travel?: Record<string, unknown> | null;
  italy_work?: Record<string, unknown> | null;
} | null | undefined): Answers {
  const next: Answers = {};
  const text = (page: Record<string, unknown> | null | undefined, key: string, id: string) => {
    const value = page?.[key];
    if (typeof value === "string" && value.trim()) next[id] = value;
  };
  const span = (page: Record<string, unknown> | null | undefined, key: string, id: string) => {
    const value = page?.[key];
    if (!value || typeof value !== "object") return;
    const dates = value as { start?: string | null; end?: string | null };
    if (dates.start || dates.end) next[id] = { start: dates.start || "", end: dates.end || "" };
  };
  const your = data?.your_work;
  text(your, "role", "pf.engagement.your_role");
  text(your, "duties", "pf.engagement.duties");
  text(your, "film_or_television", "pf.us.mptv_services");
  text(your, "audiovisual_project_role", "pf.es.audiovisual_project_role");
  const hiring = data?.hiring_and_pay;
  text(hiring, "hiring_company", "pf.engagement.hiring_company");
  text(hiring, "hiring_company_role", "pf.engagement.hiring_company_role");
  text(hiring, "same_as_production_company", "pf.engagement.hiring_company_is_production_company");
  text(hiring, "engagement_type", "pf.engagement.engagement_type");
  text(hiring, "paid", "pf.engagement.paid");
  text(hiring, "ownership_interest", "pf.us.ownership_interest");
  const compensation = hiring?.compensation;
  if (compensation && typeof compensation === "object") {
    const money = compensation as { amount?: string | null; currency?: string | null };
    if (money.amount || money.currency) next["pf.engagement.compensation"] = { amount: money.amount || "", currency: money.currency || "" };
  }
  const dates = data?.dates_and_travel;
  span(dates, "engagement_dates", "pf.engagement.engagement_dates");
  text(dates, "country_of_work", "pf.engagement.country_of_work");
  text(dates, "work_location", "pf.engagement.work_location");
  span(dates, "dates_in_country", "pf.engagement.dates_in_country");
  text(dates, "expected_arrival", "q.travel.expected_arrival");
  text(dates, "health_cover_source", "pf.es.health_cover_source");
  text(dates, "schengen_entry_date", "pf.es.schengen_entry_date");
  const italy = data?.italy_work;
  text(italy, "accommodation", "pf.it.accommodation");
  const insurance = italy?.health_insurance;
  if (insurance && typeof insurance === "object") {
    const cover = insurance as { answer?: string | null; details?: string | null };
    if (cover.answer || cover.details) next["pf.it.health_insurance"] = { value: cover.answer || "", explanation: cover.details || "" };
  }
  return next;
}
